/**
 * OIDC Dynamic Client Registration (RFC 7591) admin schemas.
 *
 * Wire shapes for the backend handlers in
 * backend/internal/identity/oauth/dynreg/handler.go. We keep the
 * snake_case names that the Go DTOs emit and provide convenient
 * camel-case access points downstream when needed.
 *
 * The plaintext IAT lives on the mint-response ONLY — every
 * subsequent read returns just the summary. The plaintext is
 * NEVER cached; UIs that need to display it pull it from the
 * mutation's resolved promise value and surrender it on dialog
 * close.
 */

import { z } from "zod";

// ── Response shapes (generated from the contract) ───────────

import {
  dcrPolicySchema,
  iatListResponseSchema,
  iatSummarySchema,
  mintIATResponseSchema,
  registrationReviewListSchema,
  registrationReviewSchema,
} from "./api.generated";
export {
  dcrPolicySchema,
  iatListResponseSchema,
  iatSummarySchema,
  mintIATResponseSchema,
  registrationReviewListSchema,
  registrationReviewSchema,
};

export type IATSummary = z.infer<typeof iatSummarySchema>;
export type IATListResponse = z.infer<typeof iatListResponseSchema>;
export type MintIATResponse = z.infer<typeof mintIATResponseSchema>;
export type DCRPolicy = z.infer<typeof dcrPolicySchema>;
export type RegistrationReview = z.infer<typeof registrationReviewSchema>;

// ── Mint request ──────────────────────────────────

/**
 * Mint-input form schema. The form view binds to these names;
 * runtime conversion to the wire body lives in queries.ts.
 *
 * `expiresInDays` (form) → `expires_at` (wire) — we let the
 * admin think in days because "iso 8601 timestamp" is not what
 * an operator wants to type. Zero / undefined = no expiry.
 *
 * `maxRegistrations` is optional too — empty = unlimited.
 */
export const mintIATInputSchema = z
  .object({
    label: z
      .string()
      .trim()
      .min(1, "Label is required")
      .max(120, "Label can be at most 120 characters")
      // Reject ASCII control bytes (0x00–0x1f) + DEL (0x7f) so the
      // label is safe to echo back in admin UIs and audit log lines
      // without HTML-escaping concerns. Use escape sequences here
      // — NOT literal control bytes — so the regex stays legible
      // to a reviewer skimming the file. The previous source had
      // literal control bytes embedded between the brackets, which
      // many editors render as "[ -]" and look like "space or
      // dash" — a confusing footgun even though the bytes were
      // correct.
      // eslint-disable-next-line no-control-regex -- matching control bytes is the intent: we reject them.
      .refine((v) => !/[\u0000-\u001f\u007f]/.test(v), {
        message: "Label cannot contain control characters",
      }),
    maxRegistrations: z
      .number()
      .int()
      .positive("max_registrations must be > 0")
      .optional(),
    expiresInDays: z
      .number()
      .int()
      .positive("Expiry must be > 0 days")
      .max(365, "Expiry capped at 365 days")
      .optional(),
  })
  .strict();
export type MintIATInput = z.infer<typeof mintIATInputSchema>;

// ── Policy ───────────────────────────────────────────────────

/**
 * Patch shape for the PUT endpoint. Pointer-style: undefined
 * keys are omitted from the request body so the backend
 * preserves the existing value.
 */
export const dcrPolicyPatchSchema = z
  .object({
    open_registration_enabled: z.boolean().optional(),
    allowed_redirect_uri_suffixes: z.array(z.string()).optional(),
    min_token_endpoint_auth_method: z
      .union([
        z.literal(""),
        z.literal("none"),
        z.literal("client_secret_post"),
        z.literal("client_secret_basic"),
        z.literal("private_key_jwt"),
      ])
      .optional(),
  })
  .strict();
export type DCRPolicyPatch = z.infer<typeof dcrPolicyPatchSchema>;
